/**
 * FullTemplateSelector Component
 *
 * A top-level template selector that applies all settings at once.
 * Validates that events are still available and not expired.
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { ChevronDown, Save, Trash2, Layers, AlertCircle, Check } from 'lucide-react';
import type { FullEmailTemplate } from '../types/emailTemplates';
import type { HMCEvent } from '../hmcEventsStore';
import { TemplateSavePopover } from './TemplateSavePopover';

interface FullTemplateSelectorProps {
  /** Available full templates */
  templates: FullEmailTemplate[];
  /** Current events available (for validation) */
  events: HMCEvent[];
  /** Current form state (to save) */
  currentState: Omit<FullEmailTemplate, 'id' | 'name' | 'createdAt' | 'updatedAt'>;
  /** Callback when a template is applied */
  onApply: (template: FullEmailTemplate, validEventIds: {
    sporting: number[];
    concert: number[];
    other: number[];
  }) => void;
  /** Callback to save new template */
  onSave: (name: string, state: Omit<FullEmailTemplate, 'id' | 'name' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  /** Callback to delete a template */
  onDelete: (id: string) => Promise<void>;
}

export const FullTemplateSelector: React.FC<FullTemplateSelectorProps> = ({
  templates,
  events,
  currentState,
  onApply,
  onSave,
  onDelete,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSavePopoverOpen, setIsSavePopoverOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get valid event IDs (events that still exist)
  const validEventIds = useMemo(() => {
    const eventIdSet = new Set(events.map((e) => e.id));
    return eventIdSet;
  }, [events]);

  // Check if a template has all valid events
  const getTemplateValidation = useCallback((template: FullEmailTemplate) => {
    const sportingValid = template.sportingEventIds.filter((id) => validEventIds.has(id));
    const concertValid = template.concertEventIds.filter((id) => validEventIds.has(id));
    const otherValid = template.otherEventIds.filter((id) => validEventIds.has(id));

    const totalRequested = template.sportingEventIds.length + template.concertEventIds.length + template.otherEventIds.length;
    const totalValid = sportingValid.length + concertValid.length + otherValid.length;

    return {
      sportingValid,
      concertValid,
      otherValid,
      totalValid,
      totalRequested,
      isFullyValid: totalValid === totalRequested,
      hasAnyValid: totalValid > 0,
    };
  }, [validEventIds]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  // Handle template apply
  const handleApply = useCallback((template: FullEmailTemplate) => {
    const validation = getTemplateValidation(template);
    onApply(template, {
      sporting: validation.sportingValid,
      concert: validation.concertValid,
      other: validation.otherValid,
    });
    setIsDropdownOpen(false);
  }, [onApply, getTemplateValidation]);

  // Handle save
  const handleSave = useCallback(async (name: string) => {
    await onSave(name, currentState);
  }, [onSave, currentState]);

  // Handle delete
  const handleDelete = useCallback(async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingId(id);
    try {
      await onDelete(id);
    } finally {
      setDeletingId(null);
    }
  }, [onDelete]);

  // Check if we have any events selected to save
  const hasEventsSelected = currentState.sportingEventIds.length > 0 ||
    currentState.concertEventIds.length > 0 ||
    currentState.otherEventIds.length > 0;

  return (
    <div ref={dropdownRef} className="relative">
      <div className="flex items-center gap-2">
        {/* Template Dropdown Button */}
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30 rounded-lg text-purple-400 hover:from-purple-500/30 hover:to-blue-500/30 transition-all"
        >
          <Layers className="w-4 h-4" />
          <span className="text-sm font-medium">
            {templates.length > 0 ? 'Load Template' : 'No Templates'}
          </span>
          <ChevronDown className={`w-4 h-4 ${isDropdownOpen ? 'rotate-180' : ''} transition-transform`} />
        </button>

        {/* Save Button */}
        <button
          onClick={() => setIsSavePopoverOpen(true)}
          disabled={!hasEventsSelected}
          className="flex items-center gap-2 px-3 py-2 bg-purple-500 text-white rounded-lg text-sm font-medium hover:bg-purple-600 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Save current configuration as template"
        >
          <Save className="w-4 h-4" />
          <span>Save Template</span>
        </button>
      </div>

      {/* Dropdown */}
      {isDropdownOpen && (
        <div className="absolute z-50 top-full left-0 mt-2 bg-dark-surface border border-dark-border/30 rounded-xl shadow-2xl min-w-[320px] max-w-[400px] max-h-[350px] overflow-hidden">
          <div className="p-3 border-b border-dark-border/30">
            <h3 className="text-sm font-medium text-white">Saved Templates</h3>
            <p className="text-xs text-dark-muted mt-0.5">
              Apply all settings from a saved template
            </p>
          </div>

          {templates.length === 0 ? (
            <div className="p-6 text-center">
              <Layers className="w-8 h-8 text-dark-muted mx-auto mb-2" />
              <p className="text-sm text-dark-muted">No templates saved yet</p>
              <p className="text-xs text-dark-muted mt-1">
                Configure your email and click "Save Template"
              </p>
            </div>
          ) : (
            <div className="overflow-y-auto max-h-[280px]">
              {templates.map((template) => {
                const validation = getTemplateValidation(template);

                return (
                  <div
                    key={template.id}
                    onClick={() => validation.hasAnyValid && handleApply(template)}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-dark-border/30 last:border-b-0 group ${
                      validation.hasAnyValid
                        ? 'hover:bg-dark-border cursor-pointer'
                        : 'opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-white truncate">
                          {template.name}
                        </p>
                        {validation.isFullyValid ? (
                          <Check className="w-3.5 h-3.5 text-green-400 shrink-0" />
                        ) : validation.hasAnyValid ? (
                          <AlertCircle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                        ) : (
                          <AlertCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />
                        )}
                      </div>

                      {/* Template info */}
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        {template.sportingEventIds.length > 0 && (
                          <span className="px-1.5 py-0.5 bg-orange-500/20 text-orange-400 rounded text-[10px]">
                            {validation.sportingValid.length}/{template.sportingEventIds.length} sporting
                          </span>
                        )}
                        {template.concertEventIds.length > 0 && (
                          <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded text-[10px]">
                            {validation.concertValid.length}/{template.concertEventIds.length} concerts
                          </span>
                        )}
                        {template.otherEventIds.length > 0 && (
                          <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded text-[10px]">
                            {validation.otherValid.length}/{template.otherEventIds.length} other
                          </span>
                        )}
                      </div>

                      {/* Warning if some events missing */}
                      {!validation.isFullyValid && validation.hasAnyValid && (
                        <p className="text-[10px] text-amber-400 mt-1">
                          Some events no longer available
                        </p>
                      )}
                      {!validation.hasAnyValid && (
                        <p className="text-[10px] text-red-400 mt-1">
                          All events expired or unavailable
                        </p>
                      )}
                    </div>

                    {/* Delete button */}
                    <button
                      onClick={(e) => handleDelete(template.id, e)}
                      disabled={deletingId === template.id}
                      className="p-1.5 text-dark-muted hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                      title="Delete template"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Save Popover */}
      <TemplateSavePopover
        isOpen={isSavePopoverOpen}
        onClose={() => setIsSavePopoverOpen(false)}
        onSave={handleSave}
        placeholder="Template name..."
        title="Save Full Template"
      />
    </div>
  );
};

export default FullTemplateSelector;
